import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, NUGGET_TYPES, toolTransform } from '@backrs_ui/common-ui';

import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { useGetContentNuggetsBySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';

import { GuideNuggetRow } from './GuideNuggetRow';
import { useAgentContext } from './GuideToolsSection.agentContext';

type GuideToolsSectionProps = Readonly<{
  toolIds: readonly StickerCardId[];
  extraNuggets?: ContentNugget[];
  /** Content nugget slugs shown after the tools; slugs missing in this environment don't render. */
  nuggetSlugs?: readonly string[];
  /** Names this row for Lubav, so a page showing several can tell them apart. */
  contextLabel?: string;
}>;

/** Horizontally scrolling row of tool and resource cards for guide pages. */
export const GuideToolsSection: React.FC<GuideToolsSectionProps> = ({
  toolIds,
  extraNuggets,
  nuggetSlugs,
  contextLabel,
}) => {
  const navigate = useNavigate();
  const { getStickerCard } = useToolCardById({ navigate });

  const { data: slugNuggets } = useGetContentNuggetsBySlugsQuery(nuggetSlugs ?? [], {
    skip: !nuggetSlugs?.length,
  });

  const cards = useMemo(
    () => toolIds.map((toolId) => ({ toolId, card: getStickerCard(toolId) })),
    [toolIds, getStickerCard],
  );

  const nuggets: ContentNugget[] = useMemo(
    () =>
      cards.map(({ toolId, card }, index) => {
        if (card.tool) {
          return toolTransform(card.tool);
        }

        return {
          id: index,
          title: card.title,
          description: card.description ?? '',
          slug: toolId,
          star_rating: 0,
          value_rating: 0,
          variant: 'tool',
          is_browsable: false,
          type: NUGGET_TYPES.TOOL,
        };
      }),
    [cards],
  );

  const handleNuggetClick = useCallback(
    (nugget: ContentNugget) => {
      if (nugget.tool_info) {
        handlePanelNuggetClick(nugget, navigate);

        return;
      }

      // Fallback: use the sticker card's onClick when tool data isn't available
      const match = cards.find(({ toolId }) => toolId === nugget.slug);

      if (match) {
        match.card.onClick?.();

        return;
      }

      handlePanelNuggetClick(nugget, navigate);
    },
    [cards, navigate],
  );

  const allNuggets = useMemo(
    () => [...nuggets, ...(slugNuggets ?? []), ...(extraNuggets ?? [])],
    [nuggets, slugNuggets, extraNuggets],
  );

  // Only tools backed by a record carry a real id; the sticker-card fallbacks use array indexes.
  const agentNuggets = useMemo(
    () => [
      ...cards
        .map(({ card }) => card.tool)
        .filter((tool): tool is NonNullable<typeof tool> => Boolean(tool))
        .map(toolTransform),
      ...(slugNuggets ?? []),
      ...(extraNuggets ?? []),
    ],
    [cards, slugNuggets, extraNuggets],
  );

  useAgentContext({ nuggets: agentNuggets, label: contextLabel });

  return <GuideNuggetRow nuggets={allNuggets} onNuggetClick={handleNuggetClick} />;
};
